// New / Edit trigger modal. Single dialog drives 8 different action
// shapes (derive / flatten / reingest / forward_webhook / promote_to_table
// / validate_required / validate_dedupe / enrich_fetch). Action picker at
// top swaps the body fields; everything else (name, view scope, dry-run
// button) is shared.
//
// Editing semantics: changing the action bumps trigger.version and stores
// the prior version in version_history. Old derivatives keep the old
// version stamped so operators can compare in the side-by-side history
// dialog (TriggerRow's "history" button).
//
// Live table-usage probe (promote_to_table): when the operator types a
// table name we ask /workspace/table-usage if any OTHER source already
// writes to the same effective FQ table (per-source dataset + table
// name). If yes, we surface a soft warning and flag merge_key_path
// collisions. Honours per-source dataset isolation — no false positives
// on coincidental table_name reuse across sources.
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026).

import { useEffect, useState } from 'react';
import type { Trigger, TriggerAction, View } from '@datahub/shared';
import type { ViewSuggestion } from '../../api';
import {
  createTrigger,
  dryRunTrigger,
  listSecrets,
  lookupTableUsage,
  patchTrigger,
} from '../../api';
import { useToast as _useToast } from '../Toast';
import KeyPathInput from '../KeyPathInput';
import { Inp, Label, inpStyle } from '../common/atoms';
import { ActionPicker } from '../triggers/ActionPicker';
import { ValidateFieldsInput } from '../triggers/ValidateFieldsInput';
import { TriggerHelpPanel } from './TriggerHelpPanel';

void _useToast;

export function NewTriggerDialog({ sourceId, views, suggestions, onSaveSuggestion, editing, prefill, onCancel, onSave }: {
  sourceId: string;
  views: View[];
  /** Suggested views (virtual). Picking one in the Scope dropdown
   *  lazy-saves it via onSaveSuggestion before the trigger references
   *  view_id. Empty array if the parent didn't load any. */
  suggestions?: ViewSuggestion[];
  onSaveSuggestion?: (s: ViewSuggestion) => Promise<View | null>;
  editing?: Trigger;
  prefill?: { name?: string; action?: TriggerAction };
  onCancel: () => void;
  onSave: (t: Trigger) => void;
}) {
  // Editing wins over prefill; either fills initial state.
  const seed = editing?.action ?? prefill?.action;
  type ActionType = 'forward_webhook' | 'forward_webhook_batched' | 'promote_to_table' | 'validate_required' | 'validate_dedupe' | 'enrich_fetch';
  const initialType: ActionType = seed?.type ?? 'promote_to_table';
  const [name, setName] = useState(editing?.name ?? prefill?.name ?? '');
  const [type, setType] = useState<ActionType>(initialType);
  const [viewId, setViewId] = useState(editing?.view_id ?? '');
  // validate_required — iter-112: fields entries can be bare strings (legacy)
  // or {path, mode} objects. Render the dialog's CSV from the path projection;
  // mode editing is not yet exposed in this dialog, so submitted entries
  // round-trip as bare strings (mode defaults to 'enforce' server-side).
  const [validateFieldsCsv, setValidateFieldsCsv] = useState(
    seed?.type === 'validate_required'
      ? seed.fields.map(f => (typeof f === 'string' ? f : f.path)).join(', ')
      : ''
  );
  // validate_dedupe
  const [dedupeKeyPath, setDedupeKeyPath] = useState(
    seed?.type === 'validate_dedupe' ? seed.key_path : ''
  );
  const [dedupeWindowSec, setDedupeWindowSec] = useState(
    seed?.type === 'validate_dedupe' ? seed.window_sec : 3600
  );
  // enrich_fetch
  const [enrichUrl, setEnrichUrl] = useState(seed?.type === 'enrich_fetch' ? seed.url : '');
  const [enrichIdPath, setEnrichIdPath] = useState(seed?.type === 'enrich_fetch' ? seed.id_path : '');
  const [enrichMergePath, setEnrichMergePath] = useState(seed?.type === 'enrich_fetch' ? seed.merge_path : '');
  const [enrichMethod, setEnrichMethod] = useState<'GET' | 'POST'>(seed?.type === 'enrich_fetch' && seed.method ? seed.method : 'GET');
  const [enrichHeadersJson, setEnrichHeadersJson] = useState(
    seed?.type === 'enrich_fetch' && seed.headers ? JSON.stringify(seed.headers, null, 2) : ''
  );
  // Secrets vault — load existing names so the operator can click-to-insert
  // @REFs into URL and headers without leaving the dialog.
  const [vaultSecrets, setVaultSecrets] = useState<Array<{ name: string; kind: string }>>([]);
  useEffect(() => {
    if (type !== 'enrich_fetch' && type !== 'forward_webhook') return;
    listSecrets().then(rs => setVaultSecrets(rs.map(s => ({ name: s.name, kind: s.kind })))).catch(() => setVaultSecrets([]));
  }, [type]);
  // forward_webhook
  const [url, setUrl] = useState(
    seed?.type === 'forward_webhook' ? seed.url
    : seed?.type === 'forward_webhook_batched' ? seed.url
    : ''
  );
  const [method, setMethod] = useState<'POST' | 'PUT'>(
    seed?.type === 'forward_webhook' ? seed.method
    : seed?.type === 'forward_webhook_batched' ? seed.method
    : 'POST'
  );
  // forward_webhook_batched
  const [batchedBatchSize, setBatchedBatchSize] = useState(
    seed?.type === 'forward_webhook_batched' && seed.batch_size ? String(seed.batch_size) : ''
  );
  // promote_to_table
  const [tableName, setTableName] = useState(
    seed?.type === 'promote_to_table' ? seed.table_name : ''
  );
  // Live lookup: when operator types a table name, ask backend if any other
  // source already writes there. Debounced 400ms to avoid hammering the API.
  const [tableUsage, setTableUsage] = useState<{ source_count: number; sources: string[]; triggers: Array<{ source_id: string; name: string; merge_key_path?: string }> } | null>(null);
  useEffect(() => {
    if (type !== 'promote_to_table' || !tableName.trim() || !/^[a-zA-Z_][a-zA-Z0-9_]{0,99}$/.test(tableName.trim())) {
      setTableUsage(null);
      return;
    }
    const t = setTimeout(() => {
      // Pass sourceId so the server filters to triggers writing to the
      // SAME effective FQ table (dataset.table). Per-source datasets
      // mean two sources with the same table_name don't collide; the
      // older check matched on table_name alone and produced false
      // 'already exists' warnings (incident 2026-04-29).
      lookupTableUsage(tableName.trim(), sourceId).then(r => {
        // Filter out the trigger we're editing (so editing my own trigger doesn't warn against itself).
        const others = r.triggers.filter(t => !(seed && (seed as { type: 'promote_to_table'; table_name: string }).table_name === tableName.trim() && t.source_id === sourceId));
        setTableUsage({ source_count: new Set(others.map(t => t.source_id)).size, sources: [...new Set(others.map(t => t.source_id))], triggers: others });
      }).catch(() => setTableUsage(null));
    }, 400);
    return () => clearTimeout(t);
  }, [type, tableName, sourceId, seed]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<Record<string, unknown> | null>(null);

  function buildAction(): TriggerAction {
    if (type === 'promote_to_table') {
      return {
        type: 'promote_to_table',
        table_name: tableName.trim(),
      };
    } else if (type === 'validate_required') {
      return {
        type: 'validate_required',
        fields: validateFieldsCsv.split(',').map(f => f.trim()).filter(f => f.length > 0),
      };
    } else if (type === 'validate_dedupe') {
      return {
        type: 'validate_dedupe',
        key_path: dedupeKeyPath.trim(),
        window_sec: Number(dedupeWindowSec) || 3600,
      };
    } else if (type === 'enrich_fetch') {
      let parsedHeaders: Record<string, string> | undefined;
      const headerText = enrichHeadersJson.trim();
      if (headerText) {
        try { parsedHeaders = JSON.parse(headerText); } catch { /* leave undefined; backend will reject if needed */ }
      }
      return {
        type: 'enrich_fetch',
        url: enrichUrl.trim(),
        id_path: enrichIdPath.trim(),
        merge_path: enrichMergePath.trim(),
        method: enrichMethod,
        ...(parsedHeaders ? { headers: parsedHeaders } : {}),
      };
    } else if (type === 'forward_webhook_batched') {
      return {
        type: 'forward_webhook_batched',
        url: url.trim(),
        method,
        ...(batchedBatchSize ? { batch_size: Number(batchedBatchSize) } : {}),
      };
    }
    return { type: 'forward_webhook', url: url.trim(), method };
  }

  async function handleDryRun() {
    setDryRunResult(null); setError(null);
    try {
      const action = buildAction();
      let samplePayload: Record<string, unknown>;
      if (type === 'validate_required') {
        // Build a sample payload that has only the FIRST required field;
        // dry-run will report the rest as missing — operator sees the
        // failure shape before saving.
        const fieldsList = validateFieldsCsv.split(',').map(f => f.trim()).filter(Boolean);
        samplePayload = fieldsList.length > 0 ? { [fieldsList[0]!.split('.').pop()!]: 'sample' } : {};
      } else if (type === 'validate_dedupe') {
        const leaf = (dedupeKeyPath.trim() || 'order_id').split('.').pop()!;
        samplePayload = { [leaf]: 'sample-id-123' };
      } else if (type === 'enrich_fetch') {
        const leaf = (enrichIdPath.trim() || 'id').split('.').pop()!;
        samplePayload = { [leaf]: '42' };
      } else {
        samplePayload = { event: 'sample', amount: '42.50', user: 'alice' };
      }
      const r = await dryRunTrigger(sourceId, { action, sample_payload: samplePayload });
      setDryRunResult(r);
    } catch (e) {
      setError(`Dry-run: ${(e as Error).message}`);
    }
  }

  async function handleSave() {
    setSaving(true); setError(null);
    const action: TriggerAction = buildAction();
    try {
      let trigger: Trigger;
      if (editing) {
        trigger = await patchTrigger(sourceId, editing.trigger_id, { name: name.trim() } as never);
        // Action change requires a separate PATCH (different shape than name/status patch).
        // patchTrigger's narrow type doesn't expose action — call the route directly.
        const r = await fetch(`/api/v1/sources/${sourceId}/triggers/${editing.trigger_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, view_id: viewId || undefined }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        trigger = await r.json() as Trigger;
      } else {
        trigger = await createTrigger(sourceId, {
          name: name.trim() || undefined,
          action,
          view_id: viewId || undefined,
        });
      }
      onSave(trigger);
    } catch (err) {
      setError(String(err));
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'oklch(0.18 0.01 250 / 0.4)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 40,
    }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 'min(880px, calc(100vw - 48px))',
        maxHeight: 'calc(100vh - 80px)',
        display: 'flex', flexDirection: 'column',
        background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12,
        overflow: 'hidden',
      }}>
        {/* Sticky header — title + close. */}
        <div style={{
          padding: '14px 22px',
          borderBottom: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          background: 'var(--bg-elev)',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
              {editing ? `Edit trigger · v${editing.version}` : 'New trigger'}
            </div>
            {editing && (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                Action change bumps version → v{editing.version + 1}. Old derivatives keep v{editing.version}.
              </div>
            )}
          </div>
          <button
            onClick={onCancel}
            disabled={saving}
            title="Close"
            aria-label="Close dialog"
            style={{
              width: 28, height: 28, minWidth: 28, padding: 0,
              border: '1px solid var(--line)', background: 'transparent',
              borderRadius: 6, cursor: saving ? 'default' : 'pointer',
              fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--ink-3)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >×</button>
        </div>

        {/* Top strip — name + action picker, full-width. Always visible
            (not in scroll area) so the choice that drives every form
            field stays anchored. */}
        <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: 'var(--bg-elev)' }}>
          <Label>Name</Label>
          <Inp value={name} onChange={setName} placeholder="e.g. Parse amount" />
          <div style={{ marginTop: 12 }}>
            <Label>Action</Label>
            <ActionPicker type={type} onChange={setType} />
          </div>
        </div>

        {/* Two-column body: form on the left, contextual help on the
            right. Body scrolls; help column scrolls independently when
            needed. Scroll fade at the bottom of the form column hints
            there's more below the fold. */}
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(280px, 1fr)', minHeight: 0, position: 'relative' }}>
        <div className="trigger-modal-form" style={{ overflowY: 'auto', padding: '16px 22px', minHeight: 0 }}>


        {(type === 'forward_webhook' || type === 'forward_webhook_batched') && (
          <>
            <Label>URL</Label>
            <Inp value={url} onChange={setUrl} placeholder="https://example.com/webhook" />
            {vaultSecrets.length > 0 && (
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Vault refs (click to append):</span>
                {vaultSecrets.map(s => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => setUrl(v => v + '@' + s.name)}
                    style={{ padding: '2px 7px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 3, cursor: 'pointer' }}
                  >@{s.name}</button>
                ))}
              </div>
            )}
            <Label>Method</Label>
            <select value={method} onChange={e => setMethod(e.target.value as 'POST' | 'PUT')} style={inpStyle}>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
            </select>
            {type === 'forward_webhook_batched' && (
              <>
                <Label>Batch size <span style={{ color: 'var(--ink-3)', textTransform: 'none' }}>· optional</span></Label>
                <input type="number" min={1} max={1000} value={batchedBatchSize} onChange={e => setBatchedBatchSize(e.target.value)} placeholder="100 (default)" style={inpStyle} />
                <div style={{ marginTop: 4, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                  Range 1..1000. Smaller batches = more requests but smaller payloads; larger = fewer requests but bigger memory at the receiver.
                </div>
              </>
            )}
          </>
        )}



        {type === 'validate_required' && (
          <>
            <Label>Required fields <span style={{ color: 'var(--ink-3)', textTransform: 'none' }}>· comma-separated JSONPath</span></Label>
            <ValidateFieldsInput value={validateFieldsCsv} onChange={setValidateFieldsCsv} sourceId={sourceId} />
            <div style={{ marginTop: 4, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
              Empty string also counts as missing. Max 50 fields. Each segment must match <code>[a-zA-Z_][a-zA-Z0-9_]*</code>.
            </div>
          </>
        )}

        {type === 'enrich_fetch' && (
          <>
            <Label>URL <span style={{ color: 'var(--ink-3)', textTransform: 'none' }}>· {'{id}'} substituted from id_path</span></Label>
            <Inp value={enrichUrl} onChange={setEnrichUrl} placeholder="e.g. https://api.acme.com/v1/users/{id}" />
            <Label>Method</Label>
            <select value={enrichMethod} onChange={e => setEnrichMethod(e.target.value as 'GET' | 'POST')} style={inpStyle}>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </select>
            <Label>id_path <span style={{ color: 'var(--ink-3)', textTransform: 'none' }}>· payload field with the lookup id</span></Label>
            <KeyPathInput value={enrichIdPath} onChange={setEnrichIdPath} sourceId={sourceId} placeholder="user_id  ·  customer.id  ·  account.uid" />
            <Label>merge_path <span style={{ color: 'var(--ink-3)', textTransform: 'none' }}>· response merges into payload here</span></Label>
            <KeyPathInput value={enrichMergePath} onChange={setEnrichMergePath} sourceId={sourceId} placeholder="customer  ·  enrichment.profile  ·  user_data" />
            <Label>Headers <span style={{ color: 'var(--ink-3)', textTransform: 'none' }}>· optional · JSON object</span></Label>
            <textarea
              value={enrichHeadersJson}
              onChange={e => setEnrichHeadersJson(e.target.value)}
              placeholder={'{\n  "Authorization": "Bearer @ACME_API_KEY",\n  "Accept": "application/json"\n}'}
              spellCheck={false}
              style={{ width: '100%', boxSizing: 'border-box', minHeight: 80, padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12, background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6, outline: 'none', color: 'var(--ink)', resize: 'vertical' }}
            />
            {vaultSecrets.length > 0 ? (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Click to insert at cursor:</span>
                {vaultSecrets.map(s => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => setEnrichHeadersJson(v => v + (v.endsWith(' ') || !v ? '' : ' ') + '@' + s.name)}
                    style={{ padding: '2px 7px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 3, cursor: 'pointer' }}
                  >@{s.name}</button>
                ))}
              </div>
            ) : (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                Reference <code>@SECRET_NAME</code> from the <a href="/workspace" style={{ color: 'var(--accent)' }}>secrets vault</a>. The executor resolves at run time so the value never lives in the trigger config.
              </div>
            )}
          </>
        )}

        {type === 'validate_dedupe' && (
          <>
            <Label>Key path <span style={{ color: 'var(--ink-3)', textTransform: 'none' }}>· dot-notation JSONPath</span></Label>
            <KeyPathInput value={dedupeKeyPath} onChange={setDedupeKeyPath} sourceId={sourceId} placeholder="order_id  ·  user.id  ·  message_id" />
            <Label>Window seconds <span style={{ color: 'var(--ink-3)', textTransform: 'none' }}>· max 604800 (7 days)</span></Label>
            <input
              type="number"
              min={1}
              max={604800}
              value={dedupeWindowSec}
              onChange={e => setDedupeWindowSec(Number(e.target.value) || 0)}
              style={inpStyle}
            />
            <div style={{ marginTop: 4, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
              Common values: <code>3600</code> (1h), <code>86400</code> (24h), <code>604800</code> (7d).
            </div>
          </>
        )}

        {type === 'promote_to_table' && (
          <>
            <Label>Table name</Label>
            <Inp value={tableName} onChange={setTableName} placeholder="acme_deals" />
            <div style={{ marginTop: 4, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>letters, digits, underscore · 100 chars max</div>

            {tableUsage && tableUsage.source_count > 0 && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: 'oklch(0.95 0.06 75 / 0.7)', border: '1px solid oklch(0.78 0.10 75)', borderRadius: 6, fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                <strong>{tableUsage.source_count} other source{tableUsage.source_count === 1 ? '' : 's'}</strong> already write{tableUsage.source_count === 1 ? 's' : ''} into <code style={{ fontFamily: 'var(--mono)' }}>datahub.{tableName.trim()}</code>.
                Rows from each source land in the same view; distinguish via the <code style={{ fontFamily: 'var(--mono)' }}>source_id</code> column.
              </div>
            )}
            <div style={{ marginTop: 8, padding: '8px 10px', background: 'oklch(0.96 0.06 150 / 0.4)', border: '1px solid oklch(0.78 0.10 150)', borderRadius: 6, fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              The trigger creates a BigQuery <strong>view over GCS</strong> — zero fixed storage cost, pay only on query scan. New events arriving in the source are auto-included on the next query. The view exposes <em>every</em> event for the source; filter at query time via <code style={{ fontFamily: 'var(--mono)' }}>WHERE</code>.
            </div>
          </>
        )}

        {(views.length > 0 || (suggestions?.length ?? 0) > 0) && (
          <>
            <Label>Scope to View (optional)</Label>
            <select
              value={viewId}
              onChange={async e => {
                const v = e.target.value;
                if (v.startsWith('sug:') && onSaveSuggestion && suggestions) {
                  const sug = suggestions.find(s => `sug:${s.id}` === v);
                  if (!sug) return;
                  const saved = await onSaveSuggestion(sug);
                  if (saved) setViewId(saved.view_id);
                  return;
                }
                setViewId(v);
              }}
              style={inpStyle}
            >
              <option value="">— fire on every event —</option>
              {views.map(v => <option key={v.view_id} value={v.view_id}>{v.name}</option>)}
              {(suggestions?.length ?? 0) > 0 && <option disabled>──── suggested (saves on pick) ────</option>}
              {suggestions?.map(s => <option key={`sug:${s.id}`} value={`sug:${s.id}`}>✨ {s.name} ({s.estimated_count})</option>)}
            </select>
          </>
        )}

        {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 14 }}>{error}</div>}

        {dryRunResult && (
          <div style={{ marginTop: 14, padding: 12, background: 'var(--bg-sunken)', border: '1px solid var(--accent-line)', borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 6 }}>
              dry-run result
            </div>
            <pre style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-2)', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(dryRunResult, null, 2)}
            </pre>
          </div>
        )}
        </div>

        {/* Right-column help panel — scrolls independently. */}
        <div style={{ overflowY: 'auto', minHeight: 0 }}>
          <TriggerHelpPanel type={type} />
        </div>

        {/* Scroll-fade hint at the bottom of the body — only shows
            when there's more content below the fold (operator sees a
            soft gradient from transparent → bg telling them to scroll). */}
        <div aria-hidden="true" style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: 24,
          background: 'linear-gradient(to bottom, transparent, var(--bg-elev))',
          pointerEvents: 'none',
        }} />
        </div>


        {/* Sticky footer — primary actions always reachable without scroll. */}
        <div style={{
          padding: '12px 22px',
          borderTop: '1px solid var(--line)',
          display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end',
          background: 'var(--bg-elev)',
        }}>
          <button
            className="btn sm ghost"
            onClick={() => { void handleDryRun(); }}
            disabled={saving}
            title="Run the action against a sample payload — does NOT save or call any external URL"
            style={{ marginRight: 'auto', fontSize: 12 }}
          >
            Try it (dry-run)
          </button>
          <button className="btn ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn primary" onClick={() => { void handleSave(); }} disabled={saving}>{saving ? 'Saving…' : (editing ? 'Save changes' : 'Create')}</button>
        </div>
      </div>
    </div>
  );
}
